import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiSettings,
  FiList,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiX,
  FiArrowUp,
  FiArrowDown,
  FiExternalLink,
  FiSliders,
  FiLayers,
  FiMove,
  FiCalendar,
  FiClock,
  FiZap,
  FiAward,
} from "react-icons/fi";
import {
  fetchAdminGames,
  saveGameOrder,
  toggleGameActiveStatus,
  updateGameSettings,
  getGameAuditHistory,
  get2048History,
  getFruitBombHistory,
} from "../api/gamesManagementApi";
import { getEvents } from "../api/eventApi";
import { formatUtcDateTimeWithSeconds } from "../utils/dateUtils";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return formatUtcDateTimeWithSeconds(dateStr);
  } catch {
    return String(dateStr);
  }
};

export default function GamesManagement() {
  const navigate = useNavigate();

  // Navigation back handler
  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/dashboard");
    }
  };

  // Main Tab: "games" (Games List) or "audit" (Gameplay Logs)
  const [activeTab, setActiveTab] = useState("games");

  // =========================================================================
  // 🎮 Games List State
  // =========================================================================
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const [togglingGameId, setTogglingGameId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Edit Game Modal State
  const [editingGame, setEditingGame] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const iconInputRef = useRef(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    route: "",
    gameTime: 30,
    minPoints: 10,
    maxPoints: 50,
    cooldownSeconds: 180,
    isActive: true,
    sortOrder: 1,
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // =========================================================================
  // 📜 Gameplay Audit Logs State
  // =========================================================================
  const [selectedAuditGameKey, setSelectedAuditGameKey] = useState("all");
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSearch, setAuditSearch] = useState("");
  const [debouncedAuditSearch, setDebouncedAuditSearch] = useState("");
  const [auditStatusFilter, setAuditStatusFilter] = useState("all");
  const [auditPage, setAuditPage] = useState(1);
  const [auditPerPage, setAuditPerPage] = useState(20);
  const [auditPagination, setAuditPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });

  const [copiedSessionId, setCopiedSessionId] = useState("");
  const searchDebounceTimer = useRef(null);

  // Events & Global Summary State
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("all");
  const [gamesSummary, setGamesSummary] = useState(null);
  const selectedEventIdRef = useRef(selectedEventId);

  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
  }, [selectedEventId]);

  // =========================================================================
  // 🚀 Load Games
  // =========================================================================
  const loadGames = useCallback(async (showToast = false, overrideEventId = null) => {
    setGamesLoading(true);
    const activeEventFilter = overrideEventId !== null ? overrideEventId : selectedEventIdRef.current;
    try {
      const res = await fetchAdminGames({
        eventId: activeEventFilter && activeEventFilter !== "all" ? activeEventFilter : undefined,
      });
      let gamesList = [];
      if (res?.status && Array.isArray(res.data)) {
        gamesList = res.data;
      } else if (Array.isArray(res)) {
        gamesList = res;
      } else if (res?.data?.games && Array.isArray(res.data.games)) {
        gamesList = res.data.games;
      }

      if (res?.summary) {
        setGamesSummary(res.summary);
      }

      // Sort by sortOrder ascending (1, 2, 3...)
      gamesList.sort((a, b) => (Number(a.sortOrder) || 999) - (Number(b.sortOrder) || 999));

      setGames(gamesList);
      setOrderChanged(false);
      if (showToast) toast.success("Games reloaded!");
    } catch (err) {
      console.error("Failed to fetch games:", err);
      if (showToast) {
        toast.error(err.message || "Failed to load games");
      }
    } finally {
      setGamesLoading(false);
    }
  }, []);

  // Load events catalog on mount & default to running event
  useEffect(() => {
    async function initEvents() {
      try {
        const res = await getEvents({ limit: 100, page: 1, status: "all" });
        const data = res?.data || res;
        const list = data?.events || (Array.isArray(data) ? data : []);
        setEventsList(list);

        const runningEvent = list.find((e) => e.status?.toLowerCase() === "running");
        if (runningEvent) {
          const runId = runningEvent.eventId || runningEvent.id;
          setSelectedEventId(runId);
          loadGames(false, runId);
        } else if (list.length > 0) {
          const firstId = list[0].eventId || list[0].id;
          setSelectedEventId(firstId);
          loadGames(false, firstId);
        } else {
          loadGames(false, "all");
        }
      } catch (e) {
        console.warn("Failed to load events in GamesManagement:", e.message);
        loadGames(false);
      }
    }
    initEvents();
  }, [loadGames]);

  const handleEventChange = (newEvtId) => {
    setSelectedEventId(newEvtId);
    loadGames(false, newEvtId);
  };

  // =========================================================================
  // 🔢 Priority Move Up / Move Down / Drag
  // =========================================================================
  const moveGame = (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : direction === "down" ? index + 1 : index;
    if (targetIndex < 0 || targetIndex >= games.length) return;

    const newGames = [...games];
    const [movedItem] = newGames.splice(index, 1);
    newGames.splice(targetIndex, 0, movedItem);

    const updatedWithOrder = newGames.map((g, idx) => ({
      ...g,
      sortOrder: idx + 1,
    }));

    setGames(updatedWithOrder);
    setOrderChanged(true);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newGames = [...games];
    const [draggedItem] = newGames.splice(draggedIndex, 1);
    newGames.splice(dropIndex, 0, draggedItem);

    const updatedWithOrder = newGames.map((g, idx) => ({
      ...g,
      sortOrder: idx + 1,
    }));

    setGames(updatedWithOrder);
    setOrderChanged(true);
    setDraggedIndex(null);
  };

  const handleSaveOrder = async () => {
    if (games.length === 0) return;
    setSavingOrder(true);
    try {
      const res = await saveGameOrder(games);
      if (res?.status) {
        toast.success(res.message || "Game priority saved!");
        setOrderChanged(false);
        loadGames(false);
      } else {
        toast.error(res?.message || "Failed to save priority");
      }
    } catch (err) {
      toast.error(err.message || "Failed to save priority");
    } finally {
      setSavingOrder(false);
    }
  };

  // =========================================================================
  // 🔘 Toggle Active Status
  // =========================================================================
  const handleToggleStatus = async (game) => {
    const targetIdOrKey = game.id || game.gameKey;
    const newStatus = !game.isActive;
    setTogglingGameId(targetIdOrKey);

    // Optimistic UI update
    setGames((prev) =>
      prev.map((g) =>
        (g.id === game.id || g.gameKey === game.gameKey)
          ? { ...g, isActive: newStatus }
          : g
      )
    );

    try {
      const res = await toggleGameActiveStatus(targetIdOrKey, newStatus);
      if (res?.status) {
        toast.success(
          res.message || `${game.name || "Game"} ${newStatus ? "enabled" : "disabled"}`
        );
      } else {
        // Revert on error
        setGames((prev) =>
          prev.map((g) =>
            (g.id === game.id || g.gameKey === game.gameKey)
              ? { ...g, isActive: !newStatus }
              : g
          )
        );
        toast.error(res?.message || "Failed to update game status");
      }
    } catch (err) {
      setGames((prev) =>
        prev.map((g) =>
          (g.id === game.id || g.gameKey === game.gameKey)
            ? { ...g, isActive: !newStatus }
            : g
        )
      );
      toast.error(err.message || "Failed to toggle game status");
    } finally {
      setTogglingGameId(null);
    }
  };

  // =========================================================================
  // 🛠️ Edit Game Settings Modal
  // =========================================================================
  const openEditModal = (game) => {
    setEditingGame(game);
    const existingIcon = game.icon || game.iconUrl || "";
    const imageBaseUrl =
      process.env.REACT_APP_API_BASE_IMAGE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "";
    let fullIconUrl = null;
    if (existingIcon) {
      if (existingIcon.startsWith("http://") || existingIcon.startsWith("https://")) {
        fullIconUrl = existingIcon;
      } else {
        const cleanBase = imageBaseUrl.replace(/\/admin\/?$/, "").replace(/\/+$/, "");
        fullIconUrl = `${cleanBase}/${existingIcon.replace(/^\/+/, "")}`;
      }
    }

    setEditFormData({
      name: game.name || "",
      description: game.description || "",
      route: game.route || "",
      gameTime: Number(game.gameTime ?? 30),
      minPoints: Number(game.minPoints ?? 0),
      maxPoints: Number(game.maxPoints ?? 50),
      cooldownSeconds: Number(game.cooldownSeconds ?? 180),
      isActive: Boolean(game.isActive),
      sortOrder: Number(game.sortOrder ?? 1),
    });
    setIconFile(null);
    setIconPreview(fullIconUrl);
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const closeEditModal = () => {
    setEditingGame(null);
    setIconFile(null);
    setIconPreview(null);
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingGame) return;

    if (Number(editFormData.minPoints) > Number(editFormData.maxPoints)) {
      toast.error("Min Points cannot exceed Max Points!");
      return;
    }

    setSavingEdit(true);
    const targetIdOrKey = editingGame.id || editingGame.gameKey;
    try {
      const payload = {
        ...editFormData,
        route: editFormData.route && editFormData.route.trim() ? editFormData.route.trim() : null,
      };
      const res = await updateGameSettings(targetIdOrKey, payload, iconFile);
      if (res?.status) {
        toast.success(res.message || "Game settings and icon updated!");
        closeEditModal();
        loadGames(false);
      } else {
        toast.error(res?.message || "Failed to update settings");
      }
    } catch (err) {
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSavingEdit(false);
    }
  };

  // =========================================================================
  // 📜 Fetch Audit Logs
  // =========================================================================
  const fetchAuditLogsData = useCallback(async (showToast = false) => {
    setAuditLoading(true);
    try {
      let combinedLogs = [];
      let totalCount = 0;
      let totalPages = 1;

      if (selectedAuditGameKey === "all") {
        const [tapRes, boomRes, slotsRes, spinRes, res2048, fruitRes] = await Promise.allSettled([
          getGameAuditHistory("tap", {
            page: auditPage,
            perPage: auditPerPage,
            search: debouncedAuditSearch,
            status: auditStatusFilter,
          }),
          getGameAuditHistory("boomcards", {
            page: auditPage,
            perPage: auditPerPage,
            search: debouncedAuditSearch,
            status: auditStatusFilter,
          }),
          getGameAuditHistory("slots", {
            page: auditPage,
            perPage: auditPerPage,
            search: debouncedAuditSearch,
            status: auditStatusFilter,
          }),
          getGameAuditHistory("spinwheel", {
            page: auditPage,
            perPage: auditPerPage,
            search: debouncedAuditSearch,
            status: auditStatusFilter,
          }),
          get2048History({
            page: auditPage,
            perPage: auditPerPage,
            search: debouncedAuditSearch,
            status: auditStatusFilter,
          }),
          getFruitBombHistory({
            page: auditPage,
            perPage: auditPerPage,
            search: debouncedAuditSearch,
            status: auditStatusFilter,
          }),
        ]);

        const tapList =
          tapRes.status === "fulfilled" && tapRes.value?.status
            ? (Array.isArray(tapRes.value.data) ? tapRes.value.data : tapRes.value.data?.history || []).map(
                (r) => ({ ...r, gameKey: "tap", gameDisplayName: "Tap Game" })
              )
            : [];

        const boomList =
          boomRes.status === "fulfilled" && boomRes.value?.status
            ? (Array.isArray(boomRes.value.data) ? boomRes.value.data : boomRes.value.data?.history || []).map(
                (r) => ({ ...r, gameKey: "boomcards", gameDisplayName: "Boom Cards" })
              )
            : [];

        const slotsList =
          slotsRes.status === "fulfilled" && slotsRes.value?.status
            ? (Array.isArray(slotsRes.value.data) ? slotsRes.value.data : slotsRes.value.data?.history || []).map(
                (r) => ({ ...r, gameKey: "slots", gameDisplayName: "777 Slots" })
              )
            : [];

        const spinList =
          spinRes.status === "fulfilled" && spinRes.value?.status
            ? (Array.isArray(spinRes.value.data) ? spinRes.value.data : spinRes.value.data?.history || []).map(
                (r) => ({ ...r, gameKey: "spinwheel", gameDisplayName: "Lucky Spin Wheel" })
              )
            : [];

        const list2048 =
          res2048.status === "fulfilled" && res2048.value?.status
            ? (Array.isArray(res2048.value.data) ? res2048.value.data : res2048.value.data?.history || []).map(
                (r) => ({ ...r, gameKey: "2048", gameDisplayName: "2048 Game" })
              )
            : [];

        const fruitList =
          fruitRes.status === "fulfilled" && fruitRes.value?.status
            ? (Array.isArray(fruitRes.value.data) ? fruitRes.value.data : fruitRes.value.data?.history || []).map(
                (r) => ({ ...r, gameKey: "fruit_Bomb", gameDisplayName: "Fruit Bomb" })
              )
            : [];

        combinedLogs = [...tapList, ...boomList, ...slotsList, ...spinList, ...list2048, ...fruitList].sort(
          (a, b) => new Date(b.createdAt || b.startedAt || 0) - new Date(a.createdAt || a.startedAt || 0)
        );
        totalCount = combinedLogs.length;
        totalPages = Math.max(1, Math.ceil(totalCount / auditPerPage));
      } else if (selectedAuditGameKey === "2048" || selectedAuditGameKey === "game_2048") {
        const res = await get2048History({
          page: auditPage,
          perPage: auditPerPage,
          search: debouncedAuditSearch,
          status: auditStatusFilter,
        });

        if (res?.status) {
          const raw = Array.isArray(res.data) ? res.data : res.data?.history || [];
          combinedLogs = raw.map((r) => ({
            ...r,
            gameKey: "2048",
            gameDisplayName: "2048 Game",
          }));

          const serverPagination = res.pagination || res.data?.pagination || {};
          totalCount =
            typeof serverPagination.total === "number"
              ? serverPagination.total
              : typeof res.total === "number"
              ? res.total
              : combinedLogs.length;

          totalPages =
            typeof serverPagination.lastPage === "number"
              ? serverPagination.lastPage
              : typeof serverPagination.totalPages === "number"
              ? serverPagination.totalPages
              : Math.max(1, Math.ceil(totalCount / auditPerPage));
        }
      } else if (selectedAuditGameKey === "fruit_Bomb" || selectedAuditGameKey === "fruit-bomb") {
        const res = await getFruitBombHistory({
          page: auditPage,
          perPage: auditPerPage,
          search: debouncedAuditSearch,
          status: auditStatusFilter,
        });

        if (res?.status) {
          const raw = Array.isArray(res.data) ? res.data : res.data?.history || [];
          combinedLogs = raw.map((r) => ({
            ...r,
            gameKey: "fruit_Bomb",
            gameDisplayName: "Fruit Bomb",
          }));

          const serverPagination = res.pagination || res.data?.pagination || {};
          totalCount =
            typeof serverPagination.total === "number"
              ? serverPagination.total
              : typeof res.total === "number"
              ? res.total
              : combinedLogs.length;

          totalPages =
            typeof serverPagination.lastPage === "number"
              ? serverPagination.lastPage
              : typeof serverPagination.totalPages === "number"
              ? serverPagination.totalPages
              : Math.max(1, Math.ceil(totalCount / auditPerPage));
        }
      } else {
        const res = await getGameAuditHistory(selectedAuditGameKey, {
          page: auditPage,
          perPage: auditPerPage,
          search: debouncedAuditSearch,
          status: auditStatusFilter,
        });

        if (res?.status) {
          const raw = Array.isArray(res.data) ? res.data : res.data?.history || [];
          combinedLogs = raw.map((r) => ({
            ...r,
            gameKey: selectedAuditGameKey,
            gameDisplayName:
              selectedAuditGameKey === "boomcards"
                ? "Boom Cards"
                : selectedAuditGameKey === "slots"
                ? "777 Slots"
                : selectedAuditGameKey === "spinwheel"
                ? "Lucky Spin Wheel"
                : "Tap Game",
          }));

          const serverPagination = res.pagination || res.data?.pagination || {};
          totalCount =
            typeof serverPagination.total === "number"
              ? serverPagination.total
              : typeof res.total === "number"
              ? res.total
              : combinedLogs.length;

          totalPages =
            typeof serverPagination.lastPage === "number"
              ? serverPagination.lastPage
              : typeof serverPagination.totalPages === "number"
              ? serverPagination.totalPages
              : Math.max(1, Math.ceil(totalCount / auditPerPage));
        }
      }

      setAuditLogs(combinedLogs);
      setAuditPagination({
        total: totalCount,
        totalPages,
        page: auditPage,
        perPage: auditPerPage,
      });

      if (showToast) toast.success("History refreshed!");
    } catch (err) {
      console.error("Failed to load audit history:", err);
      if (showToast) toast.error(err.message || "Failed to load history");
    } finally {
      setAuditLoading(false);
    }
  }, [selectedAuditGameKey, auditPage, auditPerPage, debouncedAuditSearch, auditStatusFilter]);

  // Debounce search
  useEffect(() => {
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedAuditSearch(auditSearch);
      setAuditPage(1);
    }, 400);
    return () => clearTimeout(searchDebounceTimer.current);
  }, [auditSearch]);

  useEffect(() => {
    if (activeTab === "audit") {
      fetchAuditLogsData(false);
    }
  }, [activeTab, fetchAuditLogsData]);

  // Copy helper
  const copySessionId = (sid) => {
    if (!sid) return;
    navigator.clipboard.writeText(sid);
    setCopiedSessionId(sid);
    toast.success("Copied!");
    setTimeout(() => setCopiedSessionId(""), 2000);
  };

  // Event resolver
  const activeRunningEvent = useMemo(() => {
    return eventsList.find((e) => e.status?.toLowerCase() === "running");
  }, [eventsList]);

  const selectedEventObj = useMemo(() => {
    if (!selectedEventId || selectedEventId === "all") return null;
    return eventsList.find(
      (e) => String(e.eventId) === String(selectedEventId) || String(e.id) === String(selectedEventId)
    );
  }, [selectedEventId, eventsList]);

  const selectedEventName = selectedEventObj
    ? selectedEventObj.eventName || `Event #${selectedEventObj.id}`
    : selectedEventId === "all"
    ? "All Events"
    : "Active Event";

  // Metrics across ALL games combined
  const todaySessionsCount =
    gamesSummary?.todayTotalSessions != null
      ? gamesSummary.todayTotalSessions
      : games.reduce((acc, g) => acc + (Number(g.stats?.todaySessions) || 0), 0);

  const todayPointsCount =
    gamesSummary?.todayAwardedPoints != null
      ? gamesSummary.todayAwardedPoints
      : games.reduce((acc, g) => acc + (Number(g.stats?.todayPoints) || 0), 0);

  const eventSessionsCount =
    gamesSummary?.eventTotalSessions != null
      ? gamesSummary.eventTotalSessions
      : games.reduce((acc, g) => acc + (Number(g.stats?.totalSessions) || 0), 0);

  const eventPointsCount =
    gamesSummary?.eventTotalPoints != null
      ? gamesSummary.eventTotalPoints
      : games.reduce((acc, g) => acc + (Number(g.stats?.totalPointsAwarded) || 0), 0);

  return (
    <div className="flex bg-gray-100 min-h-screen text-gray-900 font-sans">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Full-width container matching Announcement.js layout */}
        <div className="p-5 sm:p-8 space-y-8 w-full">
          {/* Breadcrumb and Back Button */}
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 hover:text-black transition cursor-pointer p-1 -ml-1 rounded hover:bg-gray-200"
              title="Go back"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/dashboard" className="hover:text-black transition">
              Dashboard
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-gray-900 font-semibold">Games Management</span>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#ffffcc] text-[#999900] flex items-center justify-center text-2xl shadow-2xs">
                🎮
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-gray-900">
                  Games Management
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Set mobile app game display order, toggle status, and adjust reward points.
                </p>
              </div>
            </div>

            {/* Top Action Buttons */}
            <div className="flex items-center gap-2.5">
              {orderChanged && (
                <button
                  onClick={handleSaveOrder}
                  disabled={savingOrder}
                  className="px-4 py-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-xs font-bold rounded-xl shadow-xs transition flex items-center gap-1.5 cursor-pointer"
                >
                  <FiSave className="h-3.5 w-3.5" />
                  <span>{savingOrder ? "Saving..." : "Save Order"}</span>
                </button>
              )}

              <button
                onClick={() => {
                  loadGames(true);
                  if (activeTab === "audit") fetchAuditLogsData(true);
                }}
                disabled={gamesLoading || auditLoading}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs cursor-pointer"
              >
                <FiRefreshCw
                  size={13}
                  className={`h-3.5 w-3.5 ${gamesLoading || auditLoading ? "animate-spin text-[#999900]" : ""}`}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* 5 Core Items in Single Unified Row (Event Selector + Today Stats + Event Stats Across All Games) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3.5">
            {/* 1. Event Selector Card */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
                  <FiCalendar size={13} className="text-amber-600" />
                  <span>Select Event</span>
                </span>
                {selectedEventObj?.status && (
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                      selectedEventObj.status.toLowerCase() === "running"
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "bg-gray-100 text-gray-600 border border-gray-200"
                    }`}
                  >
                    {selectedEventObj.status}
                  </span>
                )}
              </div>
              <div className="mt-1.5">
                <select
                  value={selectedEventId}
                  onChange={(e) => handleEventChange(e.target.value)}
                  className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-gray-900 font-bold text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#ffff00] cursor-pointer shadow-2xs"
                >
                  {activeRunningEvent && (
                    <option value={activeRunningEvent.eventId || activeRunningEvent.id}>
                      🟢 {activeRunningEvent.eventName || `Event #${activeRunningEvent.id}`}
                    </option>
                  )}
                  <option value="all">🏆 All Events (Global)</option>
                  {eventsList
                    .filter(
                      (ev) =>
                        !activeRunningEvent ||
                        String(ev.eventId || ev.id) !==
                          String(activeRunningEvent.eventId || activeRunningEvent.id)
                    )
                    .map((ev) => {
                      const isCompleted = ev.status?.toLowerCase() === "completed";
                      return (
                        <option key={ev.eventId || ev.id} value={ev.eventId || ev.id}>
                          {isCompleted ? "⚪ " : "🟡 "}
                          {ev.eventName || `Event #${ev.id}`}
                        </option>
                      );
                    })}
                </select>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium truncate">
                Filter all games &amp; stats
              </p>
            </div>

            {/* 2. Today Total Sessions (All Games) */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-4 flex flex-col justify-between hover:border-gray-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Today Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FiClock size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-gray-900 font-mono mt-1.5">
                {todaySessionsCount.toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium">
                All games played today (UTC)
              </p>
            </div>

            {/* 3. Today's Awarded Points (All Games) */}
            <div className="bg-white rounded-2xl shadow-xs border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/20 p-4 flex flex-col justify-between hover:border-emerald-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                  Today's Awarded Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <FiZap size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-emerald-700 font-mono mt-1.5">
                +{todayPointsCount.toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-emerald-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-emerald-700/80 mt-1.5 font-medium">
                Claimed across all games
              </p>
            </div>

            {/* 4. Event's Total Sessions (All Games) */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-900 uppercase tracking-wider">
                  Event's Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold">
                  <FiLayers size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-amber-900 font-mono mt-1.5">
                {eventSessionsCount.toLocaleString()}
              </p>
              <p className="text-[11px] text-amber-800/80 mt-1.5 font-medium truncate" title={selectedEventName}>
                All rounds in {selectedEventName}
              </p>
            </div>

            {/* 5. Event's Total Points (All Games) */}
            <div className="bg-white rounded-2xl shadow-xs border border-purple-200/80 bg-gradient-to-br from-white to-purple-50/20 p-4 flex flex-col justify-between hover:border-purple-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-purple-800 uppercase tracking-wider">
                  Event's Total Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <FiAward size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-purple-700 font-mono mt-1.5">
                {eventPointsCount.toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-purple-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-purple-700/80 mt-1.5 font-medium truncate" title={selectedEventName}>
                Cumulative in {selectedEventName}
              </p>
            </div>
          </div>

          {/* Simple Tab Switcher */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1 w-fit shadow-xs">
              <button
                type="button"
                onClick={() => setActiveTab("games")}
                className={`px-4 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
                  activeTab === "games"
                    ? "bg-[#ffff00] text-black shadow-2xs"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                <FiLayers size={13} />
                <span>Games & Priority Order</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("audit")}
                className={`px-4 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
                  activeTab === "audit"
                    ? "bg-[#ffff00] text-black shadow-2xs"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                <FiList size={13} />
                <span>Gameplay History Logs</span>
              </button>
            </div>

            {activeTab === "games" && orderChanged && (
              <span className="text-xs text-amber-700 font-bold bg-amber-50 border border-amber-200 px-3 py-1 rounded-lg">
                ⚠️ Click "Save Order" to apply changes
              </span>
            )}
          </div>

          {/* ================================================================= */}
          {/* TAB 1: CLEAN & SIMPLE GAMES LIST TABLE */}
          {/* ================================================================= */}
          {activeTab === "games" && (
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
              {gamesLoading ? (
                <div className="p-12 text-center">
                  <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-xs text-gray-500 font-medium">Loading games list...</p>
                </div>
              ) : games.length === 0 ? (
                <div className="p-12 text-center">
                  <FiInfo className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-bold text-gray-800">No games found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[11px]">
                        <th className="py-3 px-4 w-28 text-center">Priority</th>
                        <th className="py-3 px-4">Game</th>
                        <th className="py-3 px-4">Game Rules</th>
                        <th className="py-3 px-4 text-center">Sessions / Pts</th>
                        <th className="py-3 px-4 text-center">App Status</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {games.map((game, index) => {
                        const isFirst = index === 0;
                        const isLast = index === games.length - 1;
                        const isToggling = togglingGameId === game.id || togglingGameId === game.gameKey;
                        const isBeingDragged = draggedIndex === index;
                        const gameNameLower = (game.name || "").toLowerCase();
                        const gameRouteLower = (game.route || "").toLowerCase();
                        const gameKeyLower = (game.gameKey || "").toLowerCase();

                        const isTap =
                          gameKeyLower === "tap" ||
                          gameNameLower.includes("tap") ||
                          gameRouteLower.includes("tap");
                        const isBoom =
                          gameKeyLower === "boomcards" ||
                          gameNameLower.includes("boom") ||
                          gameRouteLower.includes("boom");
                        const isSlots =
                          gameKeyLower === "slots" ||
                          gameNameLower.includes("slot") ||
                          gameRouteLower.includes("slot");
                        const isSpin =
                          gameKeyLower === "spinwheel" ||
                          gameNameLower.includes("spin") ||
                          gameNameLower.includes("wheel") ||
                          gameRouteLower.includes("spin") ||
                          gameRouteLower.includes("wheel");
                        const is2048 =
                          gameKeyLower === "game_2048" ||
                          gameKeyLower === "2048" ||
                          gameNameLower.includes("2048") ||
                          gameRouteLower.includes("2048");
                        const isFruitBomb =
                          gameKeyLower === "fruit_bomb" ||
                          gameKeyLower === "fruit-bomb" ||
                          gameNameLower.includes("fruit") ||
                          gameRouteLower.includes("fruit");

                        const dedicatedUrl = isBoom
                          ? "/boom-cards"
                          : isTap
                          ? "/tap-game"
                          : isSlots
                          ? "/slots-game"
                          : isSpin
                          ? "/spin-wheel"
                          : is2048
                          ? "/game/2048"
                          : isFruitBomb
                          ? "/game/fruit-bomb"
                          : `/game/${game.gameKey || game.route || game.id}`;

                        return (
                          <tr
                            key={game.id || game.gameKey || index}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            className={`hover:bg-gray-50/80 transition duration-150 ${
                              isBeingDragged ? "opacity-40 bg-amber-50" : ""
                            }`}
                          >
                            {/* Priority Column (Number + Up/Down Arrows) */}
                            <td className="py-3.5 px-4 text-center whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1.5">
                                <span
                                  className="cursor-grab text-gray-400 hover:text-gray-700 p-0.5"
                                  title="Drag to reorder"
                                >
                                  <FiMove className="h-3.5 w-3.5" />
                                </span>
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 border border-gray-200 font-mono font-black text-sm text-gray-900">
                                  #{game.sortOrder || index + 1}
                                </span>
                                <div className="flex flex-col gap-0.5">
                                  <button
                                    onClick={() => moveGame(index, "up")}
                                    disabled={isFirst}
                                    className={`p-1 rounded bg-gray-100 hover:bg-[#ffff00] text-gray-700 hover:text-black transition cursor-pointer ${
                                      isFirst ? "opacity-30 cursor-not-allowed" : ""
                                    }`}
                                    title="Move Up"
                                  >
                                    <FiArrowUp className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => moveGame(index, "down")}
                                    disabled={isLast}
                                    className={`p-1 rounded bg-gray-100 hover:bg-[#ffff00] text-gray-700 hover:text-black transition cursor-pointer ${
                                      isLast ? "opacity-30 cursor-not-allowed" : ""
                                    }`}
                                    title="Move Down"
                                  >
                                    <FiArrowDown className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </td>

                            {/* Game Info - Clickable to Game Page */}
                            <td className="py-3.5 px-4 whitespace-nowrap">
                              <Link
                                to={dedicatedUrl}
                                className="flex items-center gap-3 group cursor-pointer"
                                title={`Open ${game.name || "Game"} Page`}
                              >
                                <div className="shrink-0 relative">
                                  {game.icon ? (
                                    <img
                                      src={
                                        game.icon.startsWith("http")
                                          ? game.icon
                                          : `${(process.env.REACT_APP_API_BASE_IMAGE_URL || process.env.REACT_APP_API_BASE_URL || "http://localhost:18009").replace(/\/admin\/?$/, "").replace(/\/+$/, "")}/${game.icon.replace(/^\/+/, "")}`
                                      }
                                      alt={game.name}
                                      className="w-10 h-10 rounded-xl object-cover border border-gray-200 group-hover:scale-105 group-hover:border-amber-400 group-hover:shadow-xs transition duration-150"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = "none";
                                        if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    style={{ display: game.icon ? "none" : "flex" }}
                                    className={`w-10 h-10 rounded-xl items-center justify-center text-xl group-hover:scale-105 transition duration-150 ${
                                      isBoom
                                        ? "bg-purple-100 text-purple-800"
                                        : isTap
                                        ? "bg-amber-100 text-amber-800"
                                        : isSlots
                                        ? "bg-red-100 text-red-800"
                                        : isSpin
                                        ? "bg-indigo-100 text-indigo-800"
                                        : "bg-blue-100 text-blue-800"
                                    }`}
                                  >
                                    {isBoom ? "🃏" : isTap ? "🎮" : isSlots ? "🎰" : isSpin ? "🎡" : "🕹️"}
                                  </div>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-extrabold text-gray-900 text-sm group-hover:text-amber-700 group-hover:underline flex items-center gap-1 transition-colors">
                                      {game.name || "Game"}
                                      <FiExternalLink className="h-3.5 w-3.5 text-gray-400 group-hover:text-amber-700 transition-colors" />
                                    </span>
                                    {game.route ? (
                                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                                        /{game.route}
                                      </span>
                                    ) : (
                                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                        No Route / Coming Soon
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 max-w-xs truncate group-hover:text-gray-700">
                                    {game.description || game.rewardInfo || "—"}
                                  </p>
                                </div>
                              </Link>
                            </td>

                            {/* Game Rules (Duration, Reward, Cooldown) */}
                            <td className="py-3.5 px-4 whitespace-nowrap text-xs text-gray-700">
                              <div className="flex flex-col gap-0.5">
                                <div>
                                  <span className="text-gray-400">Time:</span>{" "}
                                  <strong className="text-gray-900">{game.gameTime ?? 30}s</strong>
                                  <span className="text-gray-300 mx-1.5">•</span>
                                  <span className="text-gray-400">Reward:</span>{" "}
                                  <strong className="text-gray-900">
                                    {game.minPoints ?? 10} - {game.maxPoints ?? 50} pts
                                  </strong>
                                </div>
                                <div className="text-[11px] text-gray-500">
                                  <span>Cooldown:</span>{" "}
                                  <strong className="text-gray-700">{game.cooldownSeconds ?? 180}s</strong>
                                </div>
                              </div>
                            </td>

                            {/* Stats */}
                            <td className="py-3.5 px-4 text-center whitespace-nowrap">
                              <div className="font-mono">
                                <span className="font-bold text-gray-800">
                                  {(game.stats?.totalSessions ?? 0).toLocaleString()}
                                </span>{" "}
                                <span className="text-gray-400 text-[11px]">sessions</span>
                              </div>
                              <div className="text-[11px] font-mono font-bold text-purple-700 mt-0.5">
                                {(game.stats?.totalPointsAwarded ?? 0).toLocaleString()} PTS
                              </div>
                            </td>

                            {/* Status Switch */}
                            <td className="py-3.5 px-4 text-center whitespace-nowrap">
                              <button
                                onClick={() => handleToggleStatus(game)}
                                disabled={isToggling}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition inline-flex items-center gap-1.5 border cursor-pointer ${
                                  game.isActive
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                                    : "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                                }`}
                              >
                                {isToggling ? (
                                  <FiRefreshCw className="h-3 w-3 animate-spin" />
                                ) : game.isActive ? (
                                  <FiCheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                                ) : (
                                  <FiXCircle className="h-3.5 w-3.5 text-red-600" />
                                )}
                                <span>{game.isActive ? "Active" : "Disabled"}</span>
                              </button>
                            </td>

                            {/* Actions */}
                            <td className="py-3.5 px-4 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => openEditModal(game)}
                                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                                  title="Edit Game Settings"
                                >
                                  <FiSettings className="h-3.5 w-3.5" />
                                  <span>Edit</span>
                                </button>
                                <Link
                                  to={dedicatedUrl}
                                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-gray-800 hover:text-black text-xs font-bold transition flex items-center gap-1 cursor-pointer border border-gray-200 hover:border-yellow-400"
                                  title={`Open ${game.name || "Game"} Dedicated Page`}
                                >
                                  <span>Open</span>
                                  <FiExternalLink className="h-3.5 w-3.5" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ================================================================= */}
          {/* TAB 2: CLEAN AUDIT LOGS TABLE */}
          {/* ================================================================= */}
          {activeTab === "audit" && (
            <div className="space-y-4">
              {/* Search & Filter Toolbar */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-3.5 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    onClick={() => {
                      setSelectedAuditGameKey("all");
                      setAuditPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedAuditGameKey === "all"
                        ? "bg-[#ffff00] text-black shadow-2xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    All Games
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAuditGameKey("tap");
                      setAuditPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedAuditGameKey === "tap"
                        ? "bg-[#ffff00] text-black shadow-2xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    🎮 Tap Game
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAuditGameKey("boomcards");
                      setAuditPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedAuditGameKey === "boomcards"
                        ? "bg-[#ffff00] text-black shadow-2xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    🃏 Boom Cards
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAuditGameKey("slots");
                      setAuditPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedAuditGameKey === "slots"
                        ? "bg-[#ffff00] text-black shadow-2xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    🎰 777 Slots
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAuditGameKey("spinwheel");
                      setAuditPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedAuditGameKey === "spinwheel"
                        ? "bg-[#ffff00] text-black shadow-2xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    🎡 Spin Wheel
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAuditGameKey("2048");
                      setAuditPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedAuditGameKey === "2048"
                        ? "bg-[#ffff00] text-black shadow-2xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    🧩 2048
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAuditGameKey("fruit_Bomb");
                      setAuditPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedAuditGameKey === "fruit_Bomb"
                        ? "bg-[#ffff00] text-black shadow-2xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    🍉 Fruit Bomb
                  </button>
                </div>

                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
                    <input
                      type="text"
                      placeholder="Search username, session ID..."
                      value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:bg-white"
                    />
                  </div>

                  <select
                    value={auditStatusFilter}
                    onChange={(e) => {
                      setAuditStatusFilter(e.target.value);
                      setAuditPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-3 py-1.5 focus:outline-none focus:border-yellow-400"
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="claimed">Claimed</option>
                    <option value="started">Started</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              {/* Audit Table */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
                {auditLoading ? (
                  <div className="p-12 text-center">
                    <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 text-xs font-semibold">Loading audit logs...</p>
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="p-12 text-center">
                    <FiInfo className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-800 font-bold text-sm">No Audit Logs Found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[11px]">
                          <th className="py-3 px-4">Game</th>
                          <th className="py-3 px-4">Session ID</th>
                          <th className="py-3 px-4">User</th>
                          <th className="py-3 px-4 text-right">Points</th>
                          <th className="py-3 px-4 text-center">Status</th>
                          <th className="py-3 px-4">Timestamp</th>
                          <th className="py-3 px-4 text-center">View</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {auditLogs.map((log, idx) => {
                          const sid = log.sessionId || log.session_id || log.id;
                          const shortSid = sid ? `${String(sid).substring(0, 10)}...` : "—";
                          const isCompleted =
                            log.status === "completed" ||
                            log.status === "Claimed" ||
                            log.status === "claimed";
                          const isBoom =
                            log.gameKey === "boomcards" ||
                            String(sid).toLowerCase().startsWith("boom_");
                          const isSlots =
                            log.gameKey === "slots" ||
                            String(sid).toLowerCase().startsWith("slot_");
                          const isSpin =
                            log.gameKey === "spinwheel" ||
                            String(sid).toLowerCase().startsWith("spin_");
                          const is2048 =
                            log.gameKey === "2048" ||
                            log.gameKey === "game_2048" ||
                            String(sid).toLowerCase().startsWith("2048_") ||
                            String(log.gameDisplayName || "").includes("2048");
                          const isFruit =
                            log.gameKey === "fruit_Bomb" ||
                            log.gameKey === "fruit-bomb" ||
                            String(sid).toLowerCase().startsWith("fruit_") ||
                            String(log.gameDisplayName || "").toLowerCase().includes("fruit");

                          const claimedPts =
                            log.claimedPoints ??
                            log.pointsAwarded ??
                            log.points ??
                            log.targetPoints ??
                            0;

                          const userId = log.userId || log.user_id || log.user?.id;
                          const userName =
                            log.username ||
                            log.userName ||
                            log.user?.username ||
                            log.user?.email ||
                            `User #${userId || "—"}`;

                          return (
                            <tr
                              key={sid || idx}
                              className="hover:bg-gray-50/70 transition duration-150"
                            >
                              <td className="py-3.5 px-4 whitespace-nowrap font-bold text-gray-800">
                                <Link
                                  to={
                                    is2048
                                      ? "/game/2048"
                                      : isFruit
                                      ? "/game/fruit-bomb"
                                      : isSlots
                                      ? "/slots-game"
                                      : isBoom
                                      ? "/boom-cards"
                                      : isSpin
                                      ? "/spin-wheel"
                                      : "/tap-game"
                                  }
                                  className="hover:underline hover:text-amber-700 transition inline-flex items-center gap-1"
                                  title="Open game page"
                                >
                                  {is2048
                                    ? "🧩 2048 Game"
                                    : isFruit
                                    ? "🍉 Fruit Bomb"
                                    : isSlots
                                    ? "🎰 777 Slots"
                                    : isBoom
                                    ? "🃏 Boom Cards"
                                    : isSpin
                                    ? "🎡 Spin Wheel"
                                    : "🎮 Tap Game"}
                                  <FiExternalLink className="h-3 w-3 text-gray-400" />
                                </Link>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap font-mono text-gray-700">
                                <div className="flex items-center gap-1.5">
                                  <span>{shortSid}</span>
                                  {sid && (
                                    <button
                                      onClick={() => copySessionId(sid)}
                                      className="text-gray-400 hover:text-black p-1 rounded transition cursor-pointer"
                                      title="Copy ID"
                                    >
                                      {copiedSessionId === sid ? (
                                        <FiCheck className="h-3.5 w-3.5 text-emerald-600" />
                                      ) : (
                                        <FiCopy className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap">
                                {userId ? (
                                  <Link
                                    to={`/user/${userId}`}
                                    className="font-bold text-gray-900 hover:text-amber-800 transition"
                                  >
                                    {userName}
                                  </Link>
                                ) : (
                                  <span className="text-gray-800 font-semibold">{userName}</span>
                                )}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-right font-mono font-bold text-purple-700">
                                +{Number(claimedPts).toLocaleString()} pts
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span
                                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                    isCompleted
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                                      : log.status === "started"
                                      ? "bg-blue-50 text-blue-700 border border-blue-300"
                                      : "bg-gray-100 text-gray-600 border border-gray-200"
                                  }`}
                                >
                                  {log.status || "Unknown"}
                                </span>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-gray-500 text-[11px]">
                                {formatDateTime(log.createdAt || log.created_at || log.timestamp)}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span className="text-[11px] text-gray-400 font-mono">
                                  #{log.id || "—"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Audit Pagination */}
                <div className="p-3.5 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
                  <div className="flex items-center gap-3">
                    <span>
                      Total: {auditPagination.total} logs
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span>Per page:</span>
                      <select
                        value={auditPerPage}
                        onChange={(e) => {
                          setAuditPerPage(Number(e.target.value));
                          setAuditPage(1);
                        }}
                        className="bg-white border border-gray-200 rounded-lg px-2 py-0.5 text-xs text-gray-700 focus:outline-none focus:border-yellow-400"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                      disabled={auditPage <= 1 || auditLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                    >
                      Prev
                    </button>
                    <span className="font-bold text-gray-800">
                      Page {auditPage} of {auditPagination.totalPages}
                    </span>
                    <button
                      onClick={() => setAuditPage((p) => Math.min(auditPagination.totalPages, p + 1))}
                      disabled={auditPage >= auditPagination.totalPages || auditLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 🛠️ EDIT GAME MODAL (CLEAN & SIMPLE) */}
      {/* ========================================================================= */}
      {editingGame && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-in fade-in">
          <div className="bg-white rounded-2xl border border-gray-200 max-w-lg w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <FiSliders className="h-5 w-5 text-amber-600" />
                  <span>Edit {editingGame.name}</span>
                </h3>
              </div>
              <button
                onClick={closeEditModal}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition cursor-pointer"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-3.5 text-xs">
              {/* Game Icon Image Upload */}
              <div>
                <label className="block text-gray-700 font-bold mb-1">Game Icon Photo</label>
                <div className="flex items-center gap-3">
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleIconChange}
                    className="w-full text-xs text-gray-500 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#ffff00] file:text-black hover:file:bg-[#e6e600] cursor-pointer"
                  />
                  {iconPreview && (
                    <div className="relative shrink-0">
                      <img
                        src={iconPreview}
                        alt="Icon Preview"
                        className="w-10 h-10 rounded-xl object-cover border border-gray-300 shadow-2xs"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIconPreview(null);
                          setIconFile(null);
                          if (iconInputRef.current) iconInputRef.current.value = "";
                        }}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition cursor-pointer"
                        title="Remove image"
                      >
                        <FiX size={10} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Game Title & Route */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-bold mb-1">Game Name</label>
                  <input
                    type="text"
                    required
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    App Route Key <span className="text-gray-400 font-normal">(tap / slots / boomcards - optional)</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.route}
                    onChange={(e) => setEditFormData({ ...editFormData, route: e.target.value })}
                    placeholder="e.g. slots (Leave empty if no route)"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs font-mono"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-700 font-bold mb-1">Description</label>
                <textarea
                  rows={2}
                  value={editFormData.description}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, description: e.target.value })
                  }
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs"
                />
              </div>

              {/* Duration & Cooldown */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    Duration (Seconds)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={600}
                    required
                    value={editFormData.gameTime}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, gameTime: Number(e.target.value) })
                    }
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    Cooldown (Seconds)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={86400}
                    required
                    value={editFormData.cooldownSeconds}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        cooldownSeconds: Number(e.target.value),
                      })
                    }
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs"
                  />
                </div>
              </div>

              {/* Points Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-bold mb-1">Min Points</label>
                  <input
                    type="number"
                    min={0}
                    required
                    value={editFormData.minPoints}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, minPoints: Number(e.target.value) })
                    }
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-1">Max Points</label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={editFormData.maxPoints}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, maxPoints: Number(e.target.value) })
                    }
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs"
                  />
                </div>
              </div>

              {/* Priority & Status */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-gray-700 font-bold mb-1">Priority Order</label>
                  <input
                    type="number"
                    min={1}
                    value={editFormData.sortOrder}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, sortOrder: Number(e.target.value) })
                    }
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white text-xs"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-1">Status</label>
                  <button
                    type="button"
                    onClick={() =>
                      setEditFormData({ ...editFormData, isActive: !editFormData.isActive })
                    }
                    className={`w-full py-2 px-3 rounded-xl font-bold flex items-center justify-center gap-1.5 border transition cursor-pointer ${
                      editFormData.isActive
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                        : "bg-red-50 text-red-700 border-red-300"
                    }`}
                  >
                    {editFormData.isActive ? "✅ Active" : "❌ Disabled"}
                  </button>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="px-5 py-2 bg-[#ffff00] hover:bg-[#e6e600] text-black rounded-xl font-bold transition flex items-center gap-1.5 shadow-xs cursor-pointer"
                >
                  {savingEdit ? (
                    <>
                      <FiRefreshCw className="h-3.5 w-3.5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FiSave className="h-3.5 w-3.5" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
